import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Download, FileText, CreditCard, Truck, Trash2, X, Printer, Eye, Mail, CheckSquare, Square, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import axiosInstance from '../../api/axios';
import SendByEmailDialog from '../../components/SendByEmailDialog';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

// Erkennt Test-/Dummy-E-Mails, damit Test-Rechnungen visuell klar
// vom Echtbetrieb getrennt werden können (Compliance / §14 UStG).
const isTestEmail = (email) => {
  if (!email) return false;
  const e = String(email).toLowerCase().trim();
  return (
    e.includes('@example.com') ||
    e.includes('@example.org') ||
    e.includes('@example.net') ||
    e.startsWith('testuser_') ||
    e.startsWith('test_') ||
    e.startsWith('test@') ||
    e.includes('@test.') ||
    e.includes('+test@')
  );
};

const isTestInvoice = (inv) =>
  Boolean(inv?.is_test) || isTestEmail(inv?.customer_email || inv?.user_email);

const Invoices = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailFiles, setEmailFiles] = useState([]);
  const [emailDefaults, setEmailDefaults] = useState({ subject: '', message: '', recipient: '' });
  // 'normal' = ohne Archiv, 'archived' = nur Archiv, 'all' = beides
  const [archiveMode, setArchiveMode] = useState('normal');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_street: '',
    customer_zip_city: '',
    items: [{ name: '', quantity: 1, price: 0 }],
    shipping: 0,
    discount: 0,
    notes: ''
  });

  useEffect(() => {
    fetchInvoices();
  }, [archiveMode]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = archiveMode === 'archived'
        ? '?only_archived=true'
        : archiveMode === 'all'
          ? '?include_archived=true'
          : '';
      const response = await axiosInstance.get(`/invoices/admin/all${params}`);
      // The API returns { invoices: [], statistics: {} }
      if (response.data && response.data.invoices) {
        setInvoices(response.data.invoices);
      } else if (Array.isArray(response.data)) {
        setInvoices(response.data);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Fehler beim Laden der Rechnungen');
      setInvoices([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      return sum + (item.quantity * item.price);
    }, 0);
    const total = subtotal + parseFloat(formData.shipping || 0) - parseFloat(formData.discount || 0);
    return { subtotal, total };
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { name: '', quantity: 1, price: 0 }]
    });
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'name' ? value : parseFloat(value) || 0;
    setFormData({ ...formData, items: newItems });
  };

  const handleCreateInvoice = async () => {
    const { subtotal, total } = calculateTotals();
    
    try {
      const invoiceData = {
        ...formData,
        items: formData.items.map(item => ({
          ...item,
          total: item.quantity * item.price
        })),
        subtotal,
        total
      };

      await axiosInstance.post('/invoices/admin/create', invoiceData);
      toast.success('Rechnung erfolgreich erstellt');
      setShowCreateModal(false);
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_street: '',
        customer_zip_city: '',
        items: [{ name: '', quantity: 1, price: 0 }],
        shipping: 0,
        discount: 0,
        notes: ''
      });
      fetchInvoices();
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error('Fehler beim Erstellen der Rechnung');
    }
  };

  const handleDownload = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axiosInstance.get(`/documents/invoice/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF heruntergeladen');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Fehler beim Herunterladen');
    }
  };

  const toggleSelect = (invoiceId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const buildInvoiceAttachment = (inv) => ({
    filename: `${inv.invoice_number || inv.id}.pdf`,
    getBlob: async () => {
      const res = await axiosInstance.get(`/documents/invoice/${inv.id}/pdf`, {
        responseType: 'blob',
      });
      return new Blob([res.data], { type: 'application/pdf' });
    },
  });

  const handleTaxModeChange = async (invoiceId, newMode) => {
    try {
      const current = invoices.find((i) => i.id === invoiceId);
      const rate = current?.tax_rate ?? 19;
      await axiosInstance.put(`/invoices/admin/${invoiceId}/tax`, {
        tax_mode: newMode,
        tax_rate: rate,
      });
      setInvoices((prev) =>
        prev.map((i) => (i.id === invoiceId ? { ...i, tax_mode: newMode, tax_rate: rate } : i))
      );
      toast.success(
        newMode === 'with_tax'
          ? 'Rechnung jetzt mit MwSt.-Ausweisung'
          : 'Rechnung jetzt als Kleinunternehmer (§19 UStG)'
      );
    } catch (e) {
      toast.error('Steuer-Modus konnte nicht geändert werden');
    }
  };

  const handleTaxRateChange = async (invoiceId, newRate) => {
    try {
      await axiosInstance.put(`/invoices/admin/${invoiceId}/tax`, {
        tax_mode: 'with_tax',
        tax_rate: newRate,
      });
      setInvoices((prev) =>
        prev.map((i) => (i.id === invoiceId ? { ...i, tax_rate: newRate } : i))
      );
      toast.success(`Steuersatz auf ${newRate}% gesetzt`);
    } catch (e) {
      toast.error('Steuersatz konnte nicht geändert werden');
    }
  };

  const openEmailSingle = (invoice) => {
    setEmailFiles([buildInvoiceAttachment(invoice)]);
    setEmailDefaults({
      recipient: invoice.customer_email || '',
      subject: `Ihre Rechnung ${invoice.invoice_number || ''} von Homeyoursoul`.trim(),
      message: `Sehr geehrte/r ${invoice.customer_name || 'Kundin / Kunde'},\n\nanbei senden wir Ihnen wie gewünscht Ihre Rechnung ${invoice.invoice_number || ''}.\n\nHerzliche Grüße\nYasemin Kurt-Yilmaz\nHomeyoursoul`,
      vars: {
        Kundenname: invoice.customer_name || '',
        Rechnungsnummer: invoice.invoice_number || '',
      },
    });
    setEmailOpen(true);
  };

  const openEmailBulk = () => {
    const selected = invoices.filter((i) => selectedIds.has(i.id));
    if (selected.length === 0) return;
    if (selected.length > 10) {
      toast.error('Maximal 10 Dateien pro E-Mail');
      return;
    }
    setEmailFiles(selected.map(buildInvoiceAttachment));
    // Pre-fill recipient if all selected invoices have the same customer email
    const emails = [...new Set(selected.map((i) => (i.customer_email || '').toLowerCase()).filter(Boolean))];
    const names = [...new Set(selected.map((i) => i.customer_name).filter(Boolean))];
    const numbers = selected.map((i) => i.invoice_number || '').filter(Boolean).join(', ');
    setEmailDefaults({
      recipient: emails.length === 1 ? emails[0] : '',
      subject: `Ihre Dokumente von Homeyoursoul`,
      message: `Guten Tag,\n\nanbei senden wir Ihnen wie gewünscht folgende Dokumente:\n${numbers}\n\nHerzliche Grüße\nYasemin Kurt-Yilmaz\nHomeyoursoul`,
      vars: {
        Kundenname: names.length === 1 ? names[0] : '',
        Rechnungsnummer: numbers,
      },
    });
    setEmailOpen(true);
  };

  const handlePrint = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axiosInstance.get(`/documents/invoice/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      
      // Open PDF in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // Fallback: Create an iframe for printing
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          iframe.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            window.URL.revokeObjectURL(url);
          }, 1000);
        };
      }
      
      toast.success('Druckdialog geöffnet');
    } catch (error) {
      console.error('Error printing PDF:', error);
      toast.error('Fehler beim Drucken');
    }
  };

  const handlePreview = async (invoiceId) => {
    try {
      const response = await axiosInstance.get(`/documents/invoice/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      
      toast.success('Vorschau geöffnet');
    } catch (error) {
      console.error('Error previewing PDF:', error);
      toast.error('Fehler bei der Vorschau');
    }
  };

  const handleCreateCreditNote = async (invoiceId) => {
    if (!window.confirm('Möchten Sie eine Gutschrift/Storno für diese Rechnung erstellen?')) return;

    try {
      await axiosInstance.post(`/documents/credit-note/${invoiceId}`);
      toast.success('Gutschrift erstellt');
      fetchInvoices();
    } catch (error) {
      console.error('Error creating credit note:', error);
      toast.error('Fehler beim Erstellen der Gutschrift');
    }
  };

  const handleCreateDeliveryNote = async (invoiceId) => {
    try {
      const response = await axiosInstance.post(`/documents/delivery-note/${invoiceId}`);
      toast.success(`Lieferschein erstellt`);
    } catch (error) {
      console.error('Error creating delivery note:', error);
      toast.error('Fehler beim Erstellen des Lieferscheins');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Möchten Sie diese Rechnung wirklich löschen?')) return;

    try {
      await axiosInstance.delete(`/invoices/admin/${invoiceId}`);
      toast.success('Rechnung gelöscht');
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  // Bulk-archive: alle Test-Rechnungen ins Archiv verschieben (soft-delete).
  // Sie sind später per "Archiv anzeigen"-Filter wieder sichtbar und einzeln
  // wiederherstellbar.
  const handleDeleteAllTests = async () => {
    try {
      const dry = await axiosInstance.post('/invoices/admin/archive-tests?dry_run=true');
      const n = dry.data.would_archive;
      if (!n) {
        toast.info('Keine Test-Rechnungen gefunden.');
        return;
      }
      const preview = (dry.data.preview || []).slice(0, 10).map(p => `• ${p.number || p.id}`).join('\n');
      const ok = window.confirm(
        `${n} Test-Rechnung(en) ins Archiv verschieben?\n\n${preview}${n > 10 ? `\n…und ${n - 10} weitere` : ''}\n\nSie bleiben in der DB und können jederzeit über „Archiv anzeigen" wiederhergestellt werden.`
      );
      if (!ok) return;
      const res = await axiosInstance.post('/invoices/admin/archive-tests');
      toast.success(res.data.message || `${res.data.archived} archiviert`);
      fetchInvoices();
    } catch (e) {
      toast.error('Archivierung fehlgeschlagen');
    }
  };

  const handleRestore = async (invoice) => {
    try {
      await axiosInstance.post(`/invoices/admin/${invoice.id}/restore`);
      toast.success(`„${invoice.invoice_number}" wiederhergestellt`);
      fetchInvoices();
    } catch (e) {
      toast.error('Wiederherstellung fehlgeschlagen');
    }
  };

  const getInvoiceTypeLabel = (type) => {
    switch (type) {
      case 'invoice': return { label: 'Rechnung', color: 'bg-blue-100 text-blue-800' };
      case 'credit_note': return { label: 'Gutschrift', color: 'bg-red-100 text-red-800' };
      case 'delivery_note': return { label: 'Lieferschein', color: 'bg-green-100 text-green-800' };
      default: return { label: type, color: 'bg-gray-100 text-gray-800' };
    }
  };

  const { subtotal, total } = calculateTotals();

  if (loading) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="max-w-7xl mx-auto">
          <p className="text-center py-12">Lädt...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-7xl mx-auto">
        {/* Goldbraune Haupt-Überschrift */}
        <div className="mb-4 p-3 md:p-4 flex items-center justify-between gap-3 flex-wrap" style={{ backgroundColor: '#C4A084' }}>
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 shrink-0 rounded flex items-center justify-center bg-white/90">
              <FileText className="h-5 w-5" style={{ color: '#C4A084' }} />
            </div>
            <div className="min-w-0">
              <h1 className="text-base md:text-xl font-bold text-white truncate">Rechnungen & Dokumente</h1>
              <p className="text-xs md:text-sm text-white/80 truncate">Verwalten Sie alle Rechnungen</p>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap w-full md:w-auto">
            <select
              value={archiveMode}
              onChange={(e) => setArchiveMode(e.target.value)}
              className="border border-white/40 rounded-none px-2 py-1.5 text-sm bg-white/95 text-[#5D4E3C] flex-1 sm:flex-none min-w-0"
              title="Welche Rechnungen anzeigen"
              data-testid="invoice-archive-mode"
            >
              <option value="normal">Aktive Rechnungen</option>
              <option value="archived">Nur Archiv</option>
              <option value="all">Alle (inkl. Archiv)</option>
            </select>
            <Button
              onClick={handleDeleteAllTests}
              variant="outline"
              size="sm"
              className="bg-white text-[#8B6F4D] hover:bg-[#F5EDE4] border-[#D4C4B0] rounded-none whitespace-nowrap"
              title="Alle als TEST erkannten Rechnungen ins Archiv verschieben (umkehrbar)"
              data-testid="invoice-archive-tests"
            >
              <Trash2 className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Tests archivieren</span>
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              size="sm"
              className="bg-white text-[#C4A084] hover:bg-white/90 rounded-none whitespace-nowrap"
            >
              <Plus className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Neu</span>
            </Button>
            <Link to="/admin">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" title="Zurück zum Admin" data-testid="admin-back">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Section: Info - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
            <FileText className="h-4 w-4 text-[#60A0D0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Hinweise</h2>
        </div>

        {/* Info Box */}
        <div className="mb-6 p-4 bg-[#F0F8FF] border border-[#C4A084] rounded-none">
          <p className="text-sm text-[#60A0D0]">
            <strong>Automatisch:</strong> Bei jeder Bestellung wird automatisch eine Rechnung erstellt (Format: HYS-Jahr-Nummer).
            Hier können Sie auch manuelle Rechnungen für Anfragen erstellen.
          </p>
        </div>

        {/* Section: Invoice List */}
        <h2 className="text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-[#8B7355]" />
          Rechnungsliste
        </h2>

        {/* Invoices List */}
        <div className="space-y-4">
          {invoices.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Noch keine Rechnungen vorhanden</p>
                <p className="text-sm text-gray-500 mt-2">
                  Rechnungen werden automatisch bei Bestellungen erstellt
                </p>
              </CardContent>
            </Card>
          ) : (
            invoices.map((invoice) => {
              const typeInfo = getInvoiceTypeLabel(invoice.invoice_type);
              const isSelected = selectedIds.has(invoice.id);
              return (
                <Card
                  key={invoice.id}
                  className={`${isSelected ? 'ring-2 ring-[#C4A084] bg-[#FAF5EE]' : ''} ${isTestInvoice(invoice) ? 'border-l-4 border-l-yellow-400' : ''} ${invoice.archived_at ? 'opacity-70 bg-gray-50' : ''}`}
                  data-testid={`invoice-card-${invoice.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {/* Selection checkbox ("Viereck") */}
                        <button
                          type="button"
                          onClick={() => toggleSelect(invoice.id)}
                          className="mt-1 flex-shrink-0 hover:opacity-70 transition-opacity"
                          aria-label={isSelected ? 'Auswahl entfernen' : 'Rechnung auswählen'}
                          data-testid={`invoice-select-${invoice.id}`}
                        >
                          {isSelected
                            ? <CheckSquare className="h-5 w-5 text-[#C4A084]" />
                            : <Square className="h-5 w-5 text-[#8B7355]" />}
                        </button>
                        <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="text-lg font-semibold">{invoice.invoice_number}</h3>
                          <span className={`text-xs px-2 py-1 rounded ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                          {isTestInvoice(invoice) && (
                            <span
                              className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded bg-yellow-300 text-yellow-900 border border-yellow-500 uppercase tracking-wide"
                              title="Diese Rechnung wurde anhand der E-Mail (z. B. @example.com / testuser_…) automatisch als Test erkannt und ist KEIN gültiges steuerliches Dokument."
                              data-testid={`invoice-test-badge-${invoice.id}`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              TEST
                            </span>
                          )}
                          {invoice.archived_at && (
                            <span
                              className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded bg-gray-200 text-gray-700 border border-gray-300"
                              title={`Archiviert am ${invoice.archived_at?.slice(0, 10)}`}
                              data-testid={`invoice-archived-badge-${invoice.id}`}
                            >
                              📦 Archiviert
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Kunde:</p>
                            <p className="font-medium">{invoice.customer_name}</p>
                            {invoice.customer_email && (
                              <p className="text-gray-500">{invoice.customer_email}</p>
                            )}
                          </div>
                          <div>
                            <p className="text-gray-600">Betrag:</p>
                            <p className="font-bold text-lg">{formatEUR(invoice.total)}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(invoice.created_at).toLocaleDateString('de-DE')}
                            </p>
                          </div>
                        </div>
                        {invoice.notes && (
                          <p className="text-sm text-gray-600 mt-2 italic">Hinweis: {invoice.notes}</p>
                        )}

                        {/* Tax-Mode Toggle (per Rechnung) */}
                        <div className="mt-3 pt-3 border-t border-[#E8DCC8] flex items-center gap-2 flex-wrap text-xs">
                          <span className="text-[#8B7355]">Steuer:</span>
                          <select
                            value={invoice.tax_mode || 'kleinunternehmer'}
                            onChange={(e) => handleTaxModeChange(invoice.id, e.target.value)}
                            className="border border-[#D4C4B0] bg-white px-2 py-1 rounded-none text-xs"
                            data-testid={`invoice-tax-${invoice.id}`}
                          >
                            <option value="kleinunternehmer">Kleinunternehmer (§19 UStG)</option>
                            <option value="with_tax">Mit MwSt. ausweisen</option>
                          </select>
                          {(invoice.tax_mode === 'with_tax') && (
                            <>
                              <span className="text-[#8B7355]">Satz:</span>
                              <select
                                value={invoice.tax_rate ?? 19}
                                onChange={(e) => handleTaxRateChange(invoice.id, parseFloat(e.target.value))}
                                className="border border-[#D4C4B0] bg-white px-2 py-1 rounded-none text-xs"
                                data-testid={`invoice-tax-rate-${invoice.id}`}
                              >
                                <option value={19}>19%</option>
                                <option value={7}>7%</option>
                                <option value={0}>0%</option>
                              </select>
                            </>
                          )}
                        </div>
                      </div>
                      </div>

                      <div className="flex flex-col gap-2 ml-4">
                        {/* Preview Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePreview(invoice.id)}
                          className="bg-sky-50/70 border-sky-200 hover:bg-sky-100 text-sky-700"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Vorschau
                        </Button>
                        
                        {/* Download Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownload(invoice.id, invoice.invoice_number)}
                          className="bg-emerald-50/70 border-emerald-200 hover:bg-emerald-100 text-emerald-700"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>

                        {/* Per E-Mail senden */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEmailSingle(invoice)}
                          className="bg-amber-50/70 border-[#C4A084] hover:bg-[#E8DCC8] text-[#5D4E3C]"
                          data-testid={`invoice-email-${invoice.id}`}
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Per E-Mail
                        </Button>
                        
                        {/* Print Button */}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrint(invoice.id, invoice.invoice_number)}
                          className="bg-violet-50/70 border-violet-200 hover:bg-violet-100 text-violet-700"
                        >
                          <Printer className="h-4 w-4 mr-2" />
                          Drucken
                        </Button>
                        
                        {invoice.invoice_type === 'invoice' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCreateCreditNote(invoice.id)}
                              className="bg-amber-50/70 border-amber-200 hover:bg-amber-100 text-amber-700"
                            >
                              <CreditCard className="h-4 w-4 mr-2" />
                              Storno
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCreateDeliveryNote(invoice.id)}
                              className="bg-teal-50/70 border-teal-200 hover:bg-teal-100 text-teal-700"
                            >
                              <Truck className="h-4 w-4 mr-2" />
                              Lieferschein
                            </Button>
                          </>
                        )}
                        
                        {invoice.archived_at ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRestore(invoice)}
                            className="bg-emerald-50/70 border-emerald-200 hover:bg-emerald-100 text-emerald-700"
                            data-testid={`invoice-restore-${invoice.id}`}
                          >
                            ↩ Wiederherstellen
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(invoice.id)}
                            className="bg-red-50/70 border-red-200 hover:bg-red-100 text-red-700"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Löschen
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Create Invoice Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 sm:p-4">
            <div
              className="bg-white rounded-t-lg sm:rounded-lg max-w-3xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto"
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
              data-testid="invoice-create-modal"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold">Neue Rechnung erstellen</h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Schließen"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Customer Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer_name">Kundenname *</Label>
                      <Input
                        id="customer_name"
                        value={formData.customer_name}
                        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                        placeholder="Max Mustermann"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer_email">Email</Label>
                      <Input
                        id="customer_email"
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                        placeholder="kunde@beispiel.de"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="customer_street">Straße & Hausnummer *</Label>
                    <Input
                      id="customer_street"
                      value={formData.customer_street}
                      onChange={(e) => setFormData({ ...formData, customer_street: e.target.value })}
                      placeholder="Musterstr. 123"
                    />
                  </div>

                  <div>
                    <Label htmlFor="customer_zip_city">PLZ & Stadt *</Label>
                    <Input
                      id="customer_zip_city"
                      value={formData.customer_zip_city}
                      onChange={(e) => setFormData({ ...formData, customer_zip_city: e.target.value })}
                      placeholder="12345 Musterstadt"
                    />
                  </div>

                  {/* Items */}
                  <div className="border-t pt-4">
                    <Label className="mb-2 block">Artikel *</Label>
                    {/* Spalten-Header — sonst sieht man im Modal nicht, was die Zahlen bedeuten */}
                    <div className="hidden sm:grid grid-cols-12 gap-2 mb-1 text-[11px] uppercase tracking-wide text-[#8B7355]">
                      <div className="col-span-5">Artikelname</div>
                      <div className="col-span-2 text-right">Menge</div>
                      <div className="col-span-2 text-right">Einzelpreis €</div>
                      <div className="col-span-2 text-right">Gesamt</div>
                      <div className="col-span-1"></div>
                    </div>
                    {formData.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                        <Input
                          className="col-span-12 sm:col-span-5"
                          placeholder="Artikelname"
                          value={item.name}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          data-testid={`invoice-item-name-${index}`}
                        />
                        <Input
                          className="col-span-4 sm:col-span-2"
                          type="number"
                          placeholder="Menge"
                          aria-label="Menge"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          data-testid={`invoice-item-qty-${index}`}
                        />
                        <Input
                          className="col-span-4 sm:col-span-2"
                          type="number"
                          step="0.01"
                          placeholder="Einzelpreis €"
                          aria-label="Einzelpreis"
                          value={item.price}
                          onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                          data-testid={`invoice-item-price-${index}`}
                        />
                        <div className="col-span-3 sm:col-span-2 flex items-center justify-end">
                          <span className="font-medium">{formatEUR((item.quantity * item.price))}</span>
                        </div>
                        {formData.items.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="col-span-1"
                            onClick={() => handleRemoveItem(index)}
                            aria-label="Artikel entfernen"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button type="button" variant="outline" size="sm" onClick={handleAddItem} className="rounded-none border-[#D4C4B0]">
                      <Plus className="h-4 w-4 mr-2" />
                      Artikel hinzufügen
                    </Button>
                  </div>

                  {/* Shipping & Discount */}
                  <div className="grid grid-cols-2 gap-4 border-t pt-4">
                    <div>
                      <Label htmlFor="shipping">Versandkosten (€)</Label>
                      <Input
                        id="shipping"
                        type="number"
                        step="0.01"
                        value={formData.shipping}
                        onChange={(e) => setFormData({ ...formData, shipping: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="discount">Rabatt (€)</Label>
                      <Input
                        id="discount"
                        type="number"
                        step="0.01"
                        value={formData.discount}
                        onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label htmlFor="notes">Notizen (intern)</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Interne Notizen zur Rechnung..."
                      rows={2}
                    />
                  </div>

                  {/* Totals */}
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Zwischensumme:</span>
                      <span>{formatEUR(subtotal)}</span>
                    </div>
                    {formData.shipping > 0 && (
                      <div className="flex justify-between text-sm">
                        <span>Versand:</span>
                        <span>{formatEUR(formData.shipping)}</span>
                      </div>
                    )}
                    {formData.discount > 0 && (
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Rabatt:</span>
                        <span>-{formatEUR(formData.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Gesamt:</span>
                      <span>{formatEUR(total)}</span>
                    </div>
                  </div>

                  {/* Actions — sticky am unteren Rand, damit der Button auf dem iPhone nie aus dem Sichtfeld scrollt */}
                  <div className="flex gap-2 justify-end sticky bottom-0 -mx-6 px-6 py-3 bg-white border-t border-[#E8DCC8]">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateModal(false)}
                      className="rounded-none border-[#D4C4B0]"
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={handleCreateInvoice}
                      className="rounded-none bg-[#C4A484] hover:bg-[#B89968] text-white"
                      disabled={!formData.customer_name || !formData.customer_street || !formData.customer_zip_city || formData.items.some(i => !i.name)}
                      data-testid="invoice-create-submit"
                    >
                      Rechnung erstellen
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk action bar — sticky at bottom when selections exist */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#C4A084] shadow-lg z-40"
          data-testid="invoice-bulk-bar"
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-[#5D4E3C] font-medium">
              <span className="inline-block bg-[#C4A084] text-white px-2 py-0.5 mr-2">{selectedIds.size}</span>
              ausgewählt
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={clearSelection}
                className="rounded-none border-[#D4C4B0] text-[#5D4E3C] hover:bg-[#F5F0E8]"
                data-testid="invoice-bulk-clear"
              >
                <X className="h-4 w-4 mr-2" /> Auswahl aufheben
              </Button>
              <Button
                onClick={openEmailBulk}
                disabled={selectedIds.size > 10}
                className="rounded-none bg-[#5D4E3C] hover:bg-[#4A3E2F] text-white"
                data-testid="invoice-bulk-email"
              >
                <Mail className="h-4 w-4 mr-2" />
                Per E-Mail senden{selectedIds.size > 10 ? ' (max. 10)' : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      <SendByEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        files={emailFiles}
        defaultSubject={emailDefaults.subject}
        defaultMessage={emailDefaults.message}
        defaultRecipient={emailDefaults.recipient}
        templateVars={emailDefaults.vars || {}}
        onSent={clearSelection}
      />
    </div>
  );
};

export default Invoices;
